import { Home, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { StructuredAddress } from '@ip/shared-types';

interface AddressInputProps {
  value?: StructuredAddress;
  onChange?: (value: StructuredAddress) => void;
  defaultAddress?: StructuredAddress;
  showClientName?: boolean;
  onSearchPlace?: (query: string) => void;
  searchLoading?: boolean;
}

const COUNTRY_OPTIONS = [
  { value: 'Polska', label: 'Polska' },
  { value: 'Deutschland', label: 'Deutschland' },
  { value: 'Cesko', label: 'Cesko' },
  { value: 'Slovensko', label: 'Slovensko' },
];

export function AddressInput({
  value = {},
  onChange,
  defaultAddress,
  showClientName = true,
  onSearchPlace,
  searchLoading,
}: AddressInputProps) {
  const update = (field: keyof StructuredAddress, val: string | undefined) => {
    onChange?.({ ...value, [field]: val || undefined });
  };

  return (
    <div className="flex flex-col gap-1.5">
      {showClientName && (
        <div className="flex w-full gap-1.5">
          <Input
            placeholder="Client name (optional)"
            value={value.clientName || ''}
            onChange={e => update('clientName', e.target.value)}
            className="flex-1"
          />
          {onSearchPlace && (
            <Button
              type="button"
              variant="outline"
              onClick={() => value.clientName && onSearchPlace(value.clientName)}
              disabled={!value.clientName || searchLoading}
            >
              {searchLoading && <Loader2 className="size-4 animate-spin" />}
              Get Address
            </Button>
          )}
        </div>
      )}
      <div className="flex w-full gap-2">
        <Input
          placeholder="City"
          value={value.city || ''}
          onChange={e => update('city', e.target.value)}
          className="flex-[2]"
        />
        <Input
          placeholder="Street"
          value={value.street || ''}
          onChange={e => update('street', e.target.value)}
          className="flex-[2]"
        />
        <Input
          placeholder="Nr"
          value={value.number || ''}
          onChange={e => update('number', e.target.value)}
          className="w-[70px]"
        />
      </div>
      <div className="flex items-center gap-2">
        <Select value={value.country || 'Polska'} onValueChange={val => update('country', val)}>
          <SelectTrigger size="sm" className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {defaultAddress && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange?.({ ...defaultAddress })}
          >
            <Home className="size-4" />
            Use Office
          </Button>
        )}
      </div>
    </div>
  );
}
